import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useTenantPath } from '@/hooks/useTenantPath';
import {
  completePayrollOnboarding,
  invitePayrollEmployee,
  listPayrollEmployees,
  listPayrollExports,
  getPayrollExportDetail,
  sendPayrollOnboardingReminder,
  type PayrollEmployeeRecord,
  fetchPayrollNewHireConfig,
  listPayrollNewHireReports,
  createPayrollNewHireReport,
  submitPayrollNewHireReport,
  type PayrollNewHireStateConfig,
  type PayrollNewHireReport,
  type PayrollNewHireReportSubmitPayload,
  fetchPayrollEntitlements,
  trackPayrollEntitlementEvent,
} from '@/lib/api/payrollBridge';
import {
  fetchTimekeepingExceptions,
  fetchPayrollPeriods,
  stagePayrollPeriodRequest,
  lockPayrollPeriodRequest,
  listTipPools,
  type PayrollPeriodSummary,
  type TimekeepingException,
} from '@/lib/api/timekeeping';
import { FORM_DEFINITIONS, STATE_FORM_LIBRARY, TEMPLATE_LIBRARY } from './library';
import { PayrollExportWizard } from './ExportWizard';

interface InviteState {
  legalName: string;
  email: string;
  workLocationState: string;
}

const DEFAULT_INVITE: InviteState = {
  legalName: '',
  email: '',
  workLocationState: 'CA',
};

const EMPTY_EMPLOYEES: PayrollEmployeeRecord[] = [];
const EMPTY_NEW_HIRE_CONFIGS: PayrollNewHireStateConfig[] = [];
const EMPTY_NEW_HIRE_REPORTS: PayrollNewHireReport[] = [];

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const STANDARD_TASKS = [
  { taskType: 'offer', assignedTo: 'manager' },
  { taskType: 'handbook', assignedTo: 'employee' },
  { taskType: 'w4', assignedTo: 'employee' },
  { taskType: 'state_w4', assignedTo: 'employee' },
  { taskType: 'i9_s1', assignedTo: 'employee' },
];

const statusClassName = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100';
    case 'blocked':
      return 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100';
    case 'in_progress':
      return 'border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100';
    default:
      return 'border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10 dark:text-muted-foreground';
  }
};

const EXCEPTION_SEVERITY_STYLES: Record<string, string> = {
  info: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
  warning:
    'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  critical:
    'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100',
};

const PERIOD_STATUS_STYLES: Record<string, string> = {
  draft: 'border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10 dark:text-muted-foreground',
  review: 'border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
  locked:
    'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100',
};

const NEW_HIRE_STATUS_STYLES: Record<'pending' | 'generated' | 'submitted', string> = {
  pending:
    'border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10 dark:text-muted-foreground',
  generated:
    'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  submitted:
    'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100',
};

type NewHireMethod = PayrollNewHireReportSubmitPayload['method'];

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }
  try {
    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(
      typeof value === 'string' ? new Date(value) : value,
    );
  } catch {
    return value;
  }
};

export const PayrollManagerDashboard = () => {
  const { t } = useTranslation(['common', 'payrollBridge']);
  const queryClient = useQueryClient();
  const { tenantPath } = useTenantPath();
  const { toast } = useToast();
  const [invite, setInvite] = useState<InviteState>(DEFAULT_INVITE);
  const [selectedState, setSelectedState] = useState<string>(
    STATE_FORM_LIBRARY[0]?.stateCode ?? DEFAULT_INVITE.workLocationState,
  );
  const [isExportWizardOpen, setExportWizardOpen] = useState(false);
  const exportsQuery = useQuery({
    queryKey: ['payroll-exports'],
    queryFn: listPayrollExports,
  });
  const [selectedExportId, setSelectedExportId] = useState<string | null>(null);
  const [selectedNewHireEmployee, setSelectedNewHireEmployee] = useState<string>('');
  const [selectedNewHireStateCode, setSelectedNewHireStateCode] = useState<string>('');
  const [selectedNewHireMethod, setSelectedNewHireMethod] = useState<NewHireMethod>('portal');
  const [newHireSubmissionForm, setNewHireSubmissionForm] = useState<Record<string, { method: NewHireMethod; reference: string }>>({});
  const exportDetailQuery = useQuery({
    queryKey: ['payroll-export-detail', selectedExportId],
    queryFn: () => getPayrollExportDetail(selectedExportId!),
    enabled: Boolean(selectedExportId),
  });

  const employeesQuery = useQuery({
    queryKey: ['payroll-employees'],
    queryFn: listPayrollEmployees,
  });
  const entitlementsQuery = useQuery({
    queryKey: ['payroll-entitlements'],
    queryFn: fetchPayrollEntitlements,
  });
  const newHireConfigQuery = useQuery({
    queryKey: ['payroll-new-hire-config'],
    queryFn: fetchPayrollNewHireConfig,
  });
  const newHireReportsQuery = useQuery({
    queryKey: ['payroll-new-hire-reports'],
    queryFn: listPayrollNewHireReports,
  });
  const tipPoolsQuery = useQuery({
    queryKey: ['tip-pools'],
    queryFn: listTipPools,
  });

  const employeesData = employeesQuery.data?.employees;
  const employees = useMemo(() => employeesData ?? EMPTY_EMPLOYEES, [employeesData]);

  const newHireConfigsData = newHireConfigQuery.data;
  const newHireConfigs = useMemo(
    () => newHireConfigsData ?? EMPTY_NEW_HIRE_CONFIGS,
    [newHireConfigsData],
  );

  const newHireReportsData = newHireReportsQuery.data;
  const newHireReports = useMemo(
    () => newHireReportsData ?? EMPTY_NEW_HIRE_REPORTS,
    [newHireReportsData],
  );

  const tipPoolStats = useMemo(() => {
    const pools = tipPoolsQuery.data ?? [];
    const active = pools.filter((pool) => pool.isActive).length;
    return {
      total: pools.length,
      active,
      inactive: pools.length - active,
    };
  }, [tipPoolsQuery.data]);

  const entitlements = entitlementsQuery.data ?? null;
  const planTier = entitlements?.planTier ?? 'bridge';
  const hasNewHireReporting = entitlements?.features?.newHireReporting ?? false;
  const canExportPush = entitlements?.features?.exportPush ?? false;
  const canGlDelivery = entitlements?.features?.glDelivery ?? false;

  const planDisplayName = useMemo(
    () =>
      t(`payrollBridge:newHire.planNames.${planTier}`, {
        defaultValue: planTier,
      }),
    [planTier, t],
  );

  const entitlementViewMutation = useMutation({
    mutationFn: trackPayrollEntitlementEvent,
  });
  const entitlementCtaMutation = useMutation({
    mutationFn: trackPayrollEntitlementEvent,
  });
  const newHireUpsellLoggedRef = useRef(false);

  const inviteMutation = useMutation({
    mutationFn: invitePayrollEmployee,
    onSuccess: () => {
      setInvite(DEFAULT_INVITE);
      void employeesQuery.refetch();
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('common:status.inviteSent', {
          defaultValue: 'Invite sent successfully.',
        }),
      });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: completePayrollOnboarding,
    onSuccess: () => {
      void employeesQuery.refetch();
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('common:status.onboardingComplete', {
          defaultValue: 'Onboarding marked complete.',
        }),
      });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  useEffect(() => {
    if (entitlementsQuery.isLoading) {
      return;
    }
    if (entitlements && !hasNewHireReporting && !newHireUpsellLoggedRef.current) {
      entitlementViewMutation.mutate({
        event: 'new_hire_reporting_view',
        metadata: { plan: planTier },
      });
      newHireUpsellLoggedRef.current = true;
    }
  }, [entitlementsQuery.isLoading, entitlements, hasNewHireReporting, planTier, entitlementViewMutation]);

  useEffect(() => {
    if (!selectedNewHireEmployee && employees.length > 0) {
      setSelectedNewHireEmployee(employees[0].id);
    }
  }, [selectedNewHireEmployee, employees]);

  useEffect(() => {
    if (!selectedNewHireStateCode && newHireConfigs.length > 0) {
      setSelectedNewHireStateCode(newHireConfigs[0].stateCode);
    }
  }, [selectedNewHireStateCode, newHireConfigs]);

  useEffect(() => {
    const config = newHireConfigs.find((entry) => entry.stateCode === selectedNewHireStateCode);
    if (config && config.submissionMethods.length > 0) {
      setSelectedNewHireMethod((previous) =>
        config.submissionMethods.includes(previous) ? previous : config.submissionMethods[0],
      );
    }
  }, [selectedNewHireStateCode, newHireConfigs]);

  const selectedNewHireConfig: PayrollNewHireStateConfig | null = useMemo(() => {
    if (!newHireConfigs.length) {
      return null;
    }
    return newHireConfigs.find((entry) => entry.stateCode === selectedNewHireStateCode) ?? newHireConfigs[0];
  }, [newHireConfigs, selectedNewHireStateCode]);


  useEffect(() => {
    if (!newHireReports.length) {
      return;
    }
    setNewHireSubmissionForm((previous) => {
      const next = { ...previous };
      newHireReports.forEach((report) => {
        if (!next[report.id]) {
          const metadata = (report.metadata ?? {}) as Record<string, unknown>;
          const methodsFromMetadata = Array.isArray(metadata.submissionMethods)
            ? (metadata.submissionMethods as NewHireMethod[])
            : undefined;
          const configMethods = newHireConfigs.find((cfg) => cfg.stateCode === report.stateCode)?.submissionMethods;
          const fallbackMethods = configMethods ?? methodsFromMetadata ?? (['portal'] as NewHireMethod[]);
          const defaultMethod = (report.submissionMethod ?? fallbackMethods[0] ?? 'portal') as NewHireMethod;
          next[report.id] = {
            method: defaultMethod,
            reference: '',
          };
        }
      });
      return next;
    });
  }, [newHireReports, newHireConfigs]);

  const newHireGenerateMutation = useMutation({
    mutationFn: createPayrollNewHireReport,
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:newHire.generateSuccess'),
      });
      queryClient.invalidateQueries({ queryKey: ['payroll-new-hire-reports'] }).catch(() => undefined);
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const newHireSubmitMutation = useMutation({
    mutationFn: (variables: { reportId: string; payload: PayrollNewHireReportSubmitPayload }) =>
      submitPayrollNewHireReport(variables.reportId, variables.payload),
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:newHire.submitSuccess'),
      });
      queryClient.invalidateQueries({ queryKey: ['payroll-new-hire-reports'] }).catch(() => undefined);
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const newHireSubmissionOptions = selectedNewHireConfig?.submissionMethods ?? [];
  const isGeneratingNewHire = newHireGenerateMutation.isLoading;
  const newHireGenerateDisabled =
    employees.length === 0 || !selectedNewHireEmployee || !selectedNewHireConfig;
  const submittingReportId =
    newHireSubmitMutation.isLoading && newHireSubmitMutation.variables
      ? newHireSubmitMutation.variables.reportId
      : null;
  const newHireReportsLoading = newHireReportsQuery.isLoading;

  const reminderMutation = useMutation({
    mutationFn: sendPayrollOnboardingReminder,
    onSuccess: (response) => {
      void employeesQuery.refetch();
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:reminders.success', {
          email: response.reminder.recipient,
          defaultValue: 'Reminder sent.',
        }),
      });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : t('common:status.tryAgain', { defaultValue: 'Please try again.' });
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description: t('payrollBridge:reminders.error', {
          message,
          defaultValue: `Unable to send reminder. ${message}`,
        }),
      });
    },
  });

  const exceptionsQuery = useQuery<TimekeepingException[]>({
    queryKey: ['timekeeping', 'exceptions'],
    queryFn: () => fetchTimekeepingExceptions({ status: ['open'] }),
  });
  const payrollPeriodsQuery = useQuery<PayrollPeriodSummary[]>({
    queryKey: ['timekeeping', 'periods'],
    queryFn: () => fetchPayrollPeriods(),
  });
  const exportPeriods = payrollPeriodsQuery.data ?? [];

  const stagePeriodMutation = useMutation<
    PayrollPeriodSummary,
    Error,
    { locationId?: string | null; startDate: Date | string; endDate: Date | string }
  >({
    mutationFn: stagePayrollPeriodRequest,
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:timekeeping.stageSuccess', {
          defaultValue: 'Payroll period staged successfully.',
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['timekeeping', 'periods'] });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('payrollBridge:timekeeping.stageError', {
                defaultValue: 'Unable to stage payroll period.',
              }),
      });
    },
  });

  const lockPeriodMutation = useMutation<PayrollPeriodSummary, Error, string>({
    mutationFn: lockPayrollPeriodRequest,
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:timekeeping.lockSuccess', {
          defaultValue: 'Payroll period locked.',
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['timekeeping', 'periods'] });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('payrollBridge:timekeeping.lockError', {
                defaultValue: 'Unable to lock payroll period.',
              }),
      });
    },
  });

  const isSubmitting = inviteMutation.isLoading || completeMutation.isLoading;

  const sortedEmployees = useMemo(() => {
    if (!employeesQuery.data?.employees) {
      return [] as PayrollEmployeeRecord[];
    }
    return [...employeesQuery.data.employees].sort((a, b) => a.legalName.localeCompare(b.legalName));
  }, [employeesQuery.data]);

  const selectedStateEntry = useMemo(
    () => STATE_FORM_LIBRARY.find((entry) => entry.stateCode === selectedState) ?? STATE_FORM_LIBRARY[0],
    [selectedState],
  );

  const selectedForms = useMemo(
    () => (selectedStateEntry?.forms ?? []).map((formId) => FORM_DEFINITIONS[formId]).filter(Boolean),
    [selectedStateEntry],
  );

  const handleStageCurrentPeriod = useCallback(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const weekday = start.getUTCDay();
    start.setUTCDate(start.getUTCDate() - weekday);
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 13);

    stagePeriodMutation.mutate({
      locationId: null,
      startDate: start,
      endDate: end,
    });
  }, [stagePeriodMutation]);

  const handleLockPeriod = useCallback(
    (periodId: string) => {
      lockPeriodMutation.mutate(periodId);
    },
    [lockPeriodMutation],
  );

  const handleInviteSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      inviteMutation.mutate({
        employee: {
          legalName: invite.legalName,
          email: invite.email,
          preferredName: invite.legalName.split(' ')[0] ?? invite.legalName,
          address: {
            line1: '123 Example St',
            city: 'San Francisco',
            state: invite.workLocationState,
            postalCode: '94105',
            country: 'US',
          },
          workLocationState: invite.workLocationState,
          hireDate: new Date().toISOString().slice(0, 10),
          employmentType: 'w2',
        },
        tasks: STANDARD_TASKS,
      });
    },
    [invite, inviteMutation],
  );

  const handleGenerateNewHireReport = useCallback(() => {
    if (!hasNewHireReporting) {
      return;
    }
    if (!selectedNewHireEmployee || !selectedNewHireConfig) {
      return;
    }
    newHireGenerateMutation.mutate({
      employeeId: selectedNewHireEmployee,
      stateCode: selectedNewHireConfig.stateCode,
      submissionMethod: selectedNewHireMethod,
    });
  }, [hasNewHireReporting, selectedNewHireEmployee, selectedNewHireConfig, selectedNewHireMethod, newHireGenerateMutation]);

  const handleSubmissionMethodChange = useCallback(
    (reportId: string, method: NewHireMethod) => {
      setNewHireSubmissionForm((previous) => ({
        ...previous,
        [reportId]: {
          method,
          reference: previous[reportId]?.reference ?? '',
        },
      }));
    },
    [],
  );

  const handleSubmissionReferenceChange = useCallback((reportId: string, reference: string) => {
    setNewHireSubmissionForm((previous) => ({
      ...previous,
      [reportId]: {
        method: previous[reportId]?.method ?? ('portal' as NewHireMethod),
        reference,
      },
    }));
  }, []);

  const handleSubmitNewHireReport = useCallback(
    (report: PayrollNewHireReport) => {
      const submission = newHireSubmissionForm[report.id];
      if (!submission) {
        return;
      }
      newHireSubmitMutation.mutate({
        reportId: report.id,
        payload: {
          method: submission.method,
          submittedAt: new Date().toISOString(),
          reference: submission.reference ? submission.reference : undefined,
        },
      });
    },
    [newHireSubmissionForm, newHireSubmitMutation],
  );

  const handleUpsellClick = useCallback(() => {
    entitlementCtaMutation.mutate({
      event: 'new_hire_reporting_cta',
      metadata: { plan: planTier },
    });
    if (typeof window !== 'undefined') {
      window.open('mailto:payroll@braise.com', '_blank');
    }
  }, [entitlementCtaMutation, planTier]);

  const renderTaskStatus = useCallback(
    (status: string) =>
      t(`payrollBridge:onboardingTable.status.${status as 'pending'}`, {
        defaultValue: status,
      }),
    [t],
  );

  const renderAssignment = useCallback(
    (assignedTo: string | null | undefined) =>
      t(
        `payrollBridge:onboardingTable.assigned.${assignedTo === 'manager' ? 'manager' : 'employee'}`,
        {
          defaultValue: assignedTo === 'manager' ? 'Manager task' : 'Employee task',
        },
      ),
    [t],
  );

  const renderNewHireMethod = useCallback(
    (method: NewHireMethod) =>
      t(`payrollBridge:newHire.methods.${method}`, {
        defaultValue: method.charAt(0).toUpperCase() + method.slice(1),
      }),
    [t],
  );

  return (
    <>
      <PayrollExportWizard
        open={isExportWizardOpen}
        onOpenChange={setExportWizardOpen}
        payrollPeriods={exportPeriods}
        canExportPush={canExportPush}
        canGlDelivery={canGlDelivery}
      />
      <Dialog
        open={Boolean(selectedExportId)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedExportId(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t('payrollBridge:exports.historyDialogTitle', { defaultValue: 'Export details' })}
            </DialogTitle>
            <DialogDescription>
              {t('payrollBridge:exports.historyDialogSubtitle', {
                defaultValue: 'Review generated files and delivery events for this export run.',
              })}
            </DialogDescription>
          </DialogHeader>
          {exportDetailQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t('payrollBridge:exports.historyLoading', { defaultValue: 'Loading export history…' })}
            </p>
          ) : exportDetailQuery.data ? (
            <div className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('payrollBridge:exports.historyProvider', { defaultValue: 'Provider' })}
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {exportDetailQuery.data.export.provider.toUpperCase()}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('payrollBridge:exports.historyStatus', { defaultValue: 'Status' })}
                  </p>
                  <Badge
                    variant="secondary"
                    className={cn(
                      'border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                      exportDetailQuery.data.export.status === 'completed'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100'
                        : exportDetailQuery.data.export.status.startsWith('failed')
                          ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
                          : 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
                    )}
                  >
                    {t(`payrollBridge:exports.status.${exportDetailQuery.data.export.status}`, {
                      defaultValue: exportDetailQuery.data.export.status.replace(/_/g, ' '),
                    })}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('payrollBridge:exports.wizard.requestedAt', { defaultValue: 'Requested' })}
                  </p>
                  <p className="text-sm text-foreground">
                    {formatDateTime(exportDetailQuery.data.export.requestedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('payrollBridge:exports.wizard.completedAt', { defaultValue: 'Completed' })}
                  </p>
                  <p className="text-sm text-foreground">
                    {formatDateTime(exportDetailQuery.data.export.completedAt)}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground">
                  {t('payrollBridge:exports.historyFilesTitle', { defaultValue: 'Files' })}
                </h4>
                {(exportDetailQuery.data.files ?? []).length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('payrollBridge:exports.wizard.noFiles', {
                      defaultValue: 'No files were generated for this run.',
                    })}
                  </p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {exportDetailQuery.data.files.map((file) => (
                      <li
                        key={file.id}
                        className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 dark:border-border/60 dark:bg-muted/15"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {file.filename ?? `${file.fileType}.${(file.metadata?.format as string) ?? 'csv'}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('payrollBridge:exports.wizard.fileMeta', {
                              defaultValue: '{{type}} • {{size}}',
                              type: file.fileType,
                              size: file.sizeBytes ? `${(file.sizeBytes / 1024).toFixed(1)} KB` : '-',
                            })}
                          </p>
                        </div>
                        <Button
                          asChild
                          variant="link"
                          size="sm"
                          className="px-0"
                        >
                          <a
                            href={`/api/payroll_bridge/exports/${file.exportId}/files/${file.id}/download`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('payrollBridge:exports.wizard.downloadFile', {
                              defaultValue: 'Download',
                            })}
                          </a>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground">
                  {t('payrollBridge:exports.historyEventsTitle', { defaultValue: 'Events' })}
                </h4>
                {(exportDetailQuery.data.events ?? []).length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('payrollBridge:exports.historyNoEvents', { defaultValue: 'No events recorded.' })}
                  </p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {exportDetailQuery.data.events.map((event) => (
                      <li
                        key={event.id}
                        className="rounded-md border border-border bg-card px-3 py-2 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">
                            {event.message ?? event.eventType}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(event.createdAt)}
                          </span>
                        </div>
                        {event.payload ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {JSON.stringify(event.payload)}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('payrollBridge:exports.historyLoadError', {
                defaultValue: 'Unable to load export details.',
              })}
            </p>
          )}
        </DialogContent>
      </Dialog>
      <div className="space-y-8">
      <Alert
        variant="default"
        className="border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
      >
        <Info className="h-4 w-4 text-amber-600 dark:text-amber-300" aria-hidden="true" />
        <AlertDescription>
          {t('payrollBridge:banner.manager', {
            defaultValue:
              'Payroll Bridge does not move funds or file taxes. Verify onboarding data directly in your downstream payroll provider before running payroll.',
          })}
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>
              {t('payrollBridge:invite.title', { defaultValue: 'Invite new hire' })}
            </CardTitle>
            <CardDescription>
              {t('payrollBridge:invite.description', {
                defaultValue: 'Create an onboarding packet with the standard checklist.',
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInviteSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="invite-legal">
                  {t('payrollBridge:invite.legalName', { defaultValue: 'Legal name' })}
                </Label>
                <Input
                  id="invite-legal"
                  value={invite.legalName}
                  onChange={(event) =>
                    setInvite((current) => ({ ...current, legalName: event.target.value }))
                  }
                  placeholder="Jordan Smith"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="invite-email">
                  {t('payrollBridge:invite.email', { defaultValue: 'Email' })}
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={invite.email}
                  onChange={(event) =>
                    setInvite((current) => ({ ...current, email: event.target.value }))
                  }
                  placeholder="jordan@example.com"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="invite-state">
                  {t('payrollBridge:invite.workState', { defaultValue: 'Work location state' })}
                </Label>
                <Input
                  id="invite-state"
                  value={invite.workLocationState}
                  onChange={(event) =>
                    setInvite((current) => ({
                      ...current,
                      workLocationState: event.target.value.toUpperCase(),
                    }))
                  }
                  maxLength={2}
                  className="uppercase"
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={isSubmitting}>
                  {inviteMutation.isLoading
                    ? t('payrollBridge:invite.submitting', { defaultValue: 'Sending…' })
                    : t('payrollBridge:invite.submit', { defaultValue: 'Send invite' })}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t('payrollBridge:forms.title', { defaultValue: 'State withholding forms' })}
            </CardTitle>
            <CardDescription>
              {t('payrollBridge:forms.subtitle', {
                defaultValue:
                  'Download the required state forms and share them with the employee or manager completing onboarding.',
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="state-library">
                {t('payrollBridge:forms.selectLabel', { defaultValue: 'Work state' })}
              </Label>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger id="state-library" className="w-full">
                  <SelectValue
                    placeholder={t('payrollBridge:forms.selectLabel', {
                      defaultValue: 'Work state',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {STATE_FORM_LIBRARY.map((entry) => (
                    <SelectItem key={entry.stateCode} value={entry.stateCode}>
                      {t(`payrollBridge:${entry.labelKey}`, {
                        defaultValue: entry.stateCode,
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              {t(`payrollBridge:${selectedStateEntry?.noteKey ?? 'forms.notes.none'}`, {
                defaultValue: 'No additional state withholding form required.',
              })}
            </p>
            <div className="space-y-3">
              {selectedForms.length === 0 ? (
                <div className="rounded-md border border-dashed border-border/60 p-4 text-sm text-muted-foreground dark:border-border/40">
                  {t('payrollBridge:forms.noForms', {
                    defaultValue: 'No additional forms required for this state.',
                  })}
                </div>
              ) : (
                selectedForms.map((form) => (
                  <div
                    key={form.id}
                    className="rounded-lg border border-border bg-muted/40 p-4 dark:border-border/60 dark:bg-muted/15"
                  >
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">
                          {t(`payrollBridge:forms.formDetails.${form.id}.name`, {
                            defaultValue: form.id,
                          })}
                        </h4>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t(`payrollBridge:forms.formDetails.${form.id}.description`, {
                            defaultValue: '',
                          })}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {t(`payrollBridge:forms.formDetails.${form.id}.reminder`, {
                            defaultValue: '',
                          })}
                        </p>
                      </div>
                      {form.downloadUrl ? (
                        <Button asChild variant="outline" size="sm" className="mt-2 lg:mt-0">
                          <a
                            href={form.downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={t('payrollBridge:forms.download', {
                              defaultValue: 'Download form',
                            })}
                          >
                            {t('payrollBridge:forms.download', {
                              defaultValue: 'Download form',
                            })}
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
          <CardHeader className="space-y-1">
            <CardTitle className="text-base font-semibold text-foreground">
              {t('payrollBridge:tipPooling.cardTitle', { defaultValue: 'Automated tip pooling' })}
            </CardTitle>
            <CardDescription>
              {t('payrollBridge:tipPooling.cardDescription', {
                defaultValue: 'Distribute tips by hours, role weight, or percentage with full audit trails.',
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {tipPoolsQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : tipPoolsQuery.isError ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:tipPooling.cardError', {
                  defaultValue: 'Unable to load tip pools. Try again from the tip workspace.',
                })}
              </p>
            ) : tipPoolStats.total === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:tipPooling.cardEmpty', {
                  defaultValue: 'No tip pools configured yet. Start by creating pools for each role that participates in tips.',
                })}
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2 text-sm dark:border-border/60 dark:bg-muted/15">
                  <span>
                    {t('payrollBridge:tipPooling.activeCount', {
                      count: tipPoolStats.active,
                      defaultValue: 'Active pools',
                    })}
                  </span>
                  <Badge variant="secondary" className="px-2 py-0.5 text-xs">
                    {tipPoolStats.active}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2 text-sm dark:border-border/60 dark:bg-muted/15">
                  <span>
                    {t('payrollBridge:tipPooling.inactiveCount', {
                      count: tipPoolStats.inactive,
                      defaultValue: 'Inactive pools',
                    })}
                  </span>
                  <Badge variant="outline" className="px-2 py-0.5 text-xs">
                    {tipPoolStats.inactive}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('payrollBridge:tipPooling.cardFooter', {
                    defaultValue: 'Use the workspace to assign team members and rerun distributions for staged periods.',
                  })}
                </p>
              </div>
            )}
            <Button asChild size="sm" variant="outline">
              <a href={tenantPath('payroll/tips')}>
                {t('payrollBridge:tipPooling.manageButton', {
                  defaultValue: 'Open tip pooling workspace',
                })}
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
 
      <Card>
        <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>
              {t('payrollBridge:exports.title', {
                defaultValue: 'Provider exports & GL sync',
              })}
            </CardTitle>
            <CardDescription>
              {t('payrollBridge:exports.description', {
                defaultValue: 'Validate payroll data, generate provider files, and deliver them with a full audit trail.',
              })}
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2 lg:flex-row">
            <Button size="sm" onClick={() => setExportWizardOpen(true)}>
              {t('payrollBridge:exports.openWizard', {
                defaultValue: 'Launch export wizard',
              })}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 lg:grid-cols-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <Badge
              variant="secondary"
              className="border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100"
            >
              {t('payrollBridge:exports.highlights.validation.badge', {
                defaultValue: 'Validation',
              })}
            </Badge>
            <p className="mt-2 text-sm font-semibold text-foreground">
              {t('payrollBridge:exports.highlights.validation.title', {
                defaultValue: 'Catch blockers with the validation engine',
              })}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('payrollBridge:exports.highlights.validation.body', {
                defaultValue: 'Run rule-based checks for documents, mapping gaps, accruals, and tip variances before exporting.',
              })}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
            <Badge
              variant="secondary"
              className="border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10"
            >
              {t('payrollBridge:exports.highlights.mapping.badge', {
                defaultValue: 'Mappings',
              })}
            </Badge>
            <p className="mt-2 text-sm font-semibold text-foreground">
              {t('payrollBridge:exports.highlights.mapping.title', {
                defaultValue: 'Provider + GL templates stay in sync',
              })}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('payrollBridge:exports.highlights.mapping.body', {
                defaultValue: 'Use maintained mapping versions for Gusto, QuickBooks, Xero, and generic chart-of-accounts exports.',
              })}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
            <Badge
              variant="secondary"
              className="border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10"
            >
              {t('payrollBridge:exports.highlights.delivery.badge', {
                defaultValue: 'Delivery',
              })}
            </Badge>
            <p className="mt-2 text-sm font-semibold text-foreground">
              {t('payrollBridge:exports.highlights.delivery.title', {
                defaultValue: 'Flexible delivery methods',
              })}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('payrollBridge:exports.highlights.delivery.body', {
                defaultValue: 'Download instantly or pilot email, SFTP, and API pushes for Payroll Sync tenants.',
              })}
            </p>
          </div>
        </CardContent>
        <CardContent className="mt-4 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h4 className="text-sm font-semibold text-foreground">
              {t('payrollBridge:exports.historyTitle', { defaultValue: 'Recent exports' })}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => exportsQuery.refetch()}
              disabled={exportsQuery.isLoading || exportsQuery.isFetching}
            >
              {t('payrollBridge:exports.historyRefresh', { defaultValue: 'Refresh' })}
            </Button>
          </div>
          {exportsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t('payrollBridge:exports.historyLoading', {
                defaultValue: 'Loading export history…',
              })}
            </p>
          ) : (exportsQuery.data?.exports.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('payrollBridge:exports.historyEmpty', {
                defaultValue: 'No exports generated yet.',
              })}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('payrollBridge:exports.historyCompletedAt', { defaultValue: 'Completed' })}</TableHead>
                    <TableHead>{t('payrollBridge:exports.historyProvider', { defaultValue: 'Provider' })}</TableHead>
                    <TableHead>{t('payrollBridge:exports.historyStatus', { defaultValue: 'Status' })}</TableHead>
                    <TableHead>{t('payrollBridge:exports.historyDelivery', { defaultValue: 'Delivery' })}</TableHead>
                    <TableHead className="text-right">
                      {t('payrollBridge:exports.historyActions', { defaultValue: 'Actions' })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(exportsQuery.data?.exports ?? []).map((exportItem) => (
                    <TableRow key={exportItem.id}>
                      <TableCell>{formatDateTime(exportItem.completedAt ?? exportItem.startedAt ?? exportItem.requestedAt)}</TableCell>
                      <TableCell className="font-medium text-foreground">{exportItem.provider.toUpperCase()}</TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn(
                            'border px-2 py-0.5 text-xs font-medium uppercase tracking-wide',
                            exportItem.status === 'completed'
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100'
                              : exportItem.status.startsWith('failed')
                                ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
                                : 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
                          )}
                        >
                          {t(`payrollBridge:exports.status.${exportItem.status}`, {
                            defaultValue: exportItem.status.replace(/_/g, ' '),
                          })}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {exportItem.deliveryMethods.length > 0
                          ? exportItem.deliveryMethods.join(', ')
                          : t('payrollBridge:exports.historyDeliveryPending', { defaultValue: 'Pending' })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedExportId(exportItem.id)}
                        >
                          {t('payrollBridge:exports.historyView', { defaultValue: 'View details' })}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>
              {t('payrollBridge:onboardingTable.title', { defaultValue: 'Active onboarding' })}
            </CardTitle>
            <CardDescription>
              {t('payrollBridge:templates.description', {
                defaultValue: 'Standardize paperwork using the shared templates below.',
              })}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => employeesQuery.refetch()}
            disabled={employeesQuery.isLoading}
          >
            {t('payrollBridge:onboardingTable.refresh', { defaultValue: 'Refresh' })}
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-3 lg:grid-cols-3">
            {TEMPLATE_LIBRARY.map((template) => (
              <div
                key={template.id}
                className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
              >
                <div className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t(`payrollBridge:templates.items.${template.id}.name`, {
                      defaultValue: template.id,
                    })}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t(`payrollBridge:templates.items.${template.id}.description`, {
                      defaultValue: '',
                    })}
                  </p>
                  <Button asChild variant="outline" size="sm" className="w-fit">
                    <a
                      href={template.path}
                      download
                      aria-label={t('payrollBridge:templates.download', {
                        defaultValue: 'Download template',
                      })}
                    >
                      {t('payrollBridge:templates.download', {
                        defaultValue: 'Download template',
                      })}
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>

      {employeesQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t('payrollBridge:onboardingTable.loading', {
            defaultValue: 'Loading onboarding records…',
          })}
        </p>
      ) : sortedEmployees.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('payrollBridge:onboardingTable.noRecords', {
            defaultValue: 'No onboarding records yet.',
          })}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('payrollBridge:onboardingTable.headers.employee', {
                        defaultValue: 'Employee',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('payrollBridge:onboardingTable.headers.tasks', {
                        defaultValue: 'Tasks',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('payrollBridge:onboardingTable.headers.ssn', { defaultValue: 'SSN' })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('payrollBridge:onboardingTable.headers.actions', {
                        defaultValue: 'Actions',
                      })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedEmployees.map((employee) => {
                    const tasks = employee.tasks ?? [];
                    const completedCount = tasks.filter((task) => task.status === 'completed').length;
                    const totalCount = tasks.length;
                    const hasIncomplete = totalCount > 0 && completedCount !== totalCount;
                    const pendingAssigned = tasks.filter(
                      (task) => task.status !== 'completed' && task.assignedTo === 'employee',
                    );
                    const reminderDisabled =
                      reminderMutation.isLoading && reminderMutation.variables === employee.id;

                    return (
                      <TableRow key={employee.id}>
                        <TableCell className="align-top">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-foreground">{employee.legalName}</span>
                            <span className="text-xs text-muted-foreground">{employee.email}</span>
                            <span className="text-xs text-muted-foreground">
                              {t('payrollBridge:reminders.count', {
                                count: pendingAssigned.length,
                                defaultValue: `${pendingAssigned.length} pending`,
                              })}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex flex-wrap gap-2">
                            {tasks.map((task) => (
                              <div
                                key={task.id}
                                className="flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 dark:border-slate-800/70 dark:bg-slate-900/60"
                              >
                                <span className="text-xs font-medium text-muted-foreground">
                                  {task.taskType.toUpperCase()}
                                </span>
                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    'border px-2 py-0.5 text-[11px] font-medium',
                                    statusClassName(task.status),
                                  )}
                                >
                                  {renderTaskStatus(task.status)}
                                </Badge>
                                <span className="text-[11px] text-muted-foreground">
                                  {renderAssignment(task.assignedTo)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="align-top text-sm text-muted-foreground">
                          {employee.ssnMasked ?? '***-**-****'}
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex flex-col items-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                window.open(`/api/payroll_bridge/newhire/${employee.id}/download`, '_blank');
                              }}
                            >
                              {t('payrollBridge:onboardingTable.newHireZip', {
                                defaultValue: 'New-hire ZIP',
                              })}
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => reminderMutation.mutate(employee.id)}
                              disabled={reminderDisabled}
                            >
                              {reminderDisabled
                                ? t('payrollBridge:reminders.sending', { defaultValue: 'Sending…' })
                                : t('payrollBridge:reminders.send', { defaultValue: 'Send reminder' })}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={hasIncomplete || completeMutation.isLoading}
                              onClick={() => completeMutation.mutate(employee.id)}
                              title={
                                hasIncomplete
                                  ? t('payrollBridge:onboardingTable.completeDisabled', {
                                      defaultValue: 'Complete once all tasks are finished.',
                                    })
                                  : undefined
                              }
                            >
                              {t('payrollBridge:onboardingTable.markComplete', {
                                defaultValue: 'Mark complete',
                              })}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>
                {t('payrollBridge:timekeeping.exceptionsTitle', {
                  defaultValue: 'Timekeeping alerts',
                })}
              </CardTitle>
              <CardDescription>
                {t('payrollBridge:timekeeping.exceptionsSubtitle', {
                  defaultValue: 'Resolve open exceptions before payroll lock.',
                })}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => exceptionsQuery.refetch()} disabled={exceptionsQuery.isLoading}>
              {t('common:buttons.reload', { defaultValue: 'Reload' })}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {exceptionsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:timekeeping.loadingExceptions', { defaultValue: 'Loading exceptions…' })}
              </p>
            ) : (exceptionsQuery.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:timekeeping.noExceptions', { defaultValue: 'No open exceptions.' })}
              </p>
            ) : (
              <div className="space-y-3">
                {(exceptionsQuery.data ?? []).slice(0, 6).map((exception) => (
                  <div
                    key={exception.id}
                    className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground">{exception.summary}</span>
                          <Badge
                            variant="secondary"
                            className={
                              EXCEPTION_SEVERITY_STYLES[exception.severity] ??
                              'border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10 dark:text-muted-foreground'
                            }
                          >
                            {exception.severity.toUpperCase()}
                          </Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {exception.details ?? t('payrollBridge:timekeeping.noDetails', { defaultValue: 'No additional context provided.' })}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(exception.raisedAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>
                {t('payrollBridge:timekeeping.periodsTitle', {
                  defaultValue: 'Payroll periods',
                })}
              </CardTitle>
              <CardDescription>
                {t('payrollBridge:timekeeping.periodsSubtitle', {
                  defaultValue: 'Review staged periods and lock once exceptions are cleared.',
                })}
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={handleStageCurrentPeriod}
              disabled={stagePeriodMutation.isLoading}
            >
              {stagePeriodMutation.isLoading
                ? t('payrollBridge:timekeeping.stagePending', { defaultValue: 'Staging…' })
                : t('payrollBridge:timekeeping.stageCurrent', { defaultValue: 'Stage current period' })}
            </Button>
          </CardHeader>
          <CardContent>
            {payrollPeriodsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:timekeeping.loadingPeriods', { defaultValue: 'Loading payroll periods…' })}
              </p>
            ) : (payrollPeriodsQuery.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:timekeeping.noPeriods', { defaultValue: 'No payroll periods staged yet.' })}
              </p>
            ) : (
              <div className="space-y-3">
                {(payrollPeriodsQuery.data ?? []).slice(0, 6).map((period: PayrollPeriodSummary) => (
                  <div
                    key={period.id}
                    className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                  >
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-foreground">
                            {formatDate(period.startDate)} – {formatDate(period.endDate)}
                          </h4>
                          <Badge
                            variant="secondary"
                            className={
                              PERIOD_STATUS_STYLES[period.status] ??
                              'border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10 dark:text-muted-foreground'
                            }
                          >
                            {period.status.toUpperCase()}
                          </Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t('payrollBridge:timekeeping.hoursSummary', {
                            defaultValue: '{{hours}} regular hrs • {{overtime}} overtime hrs',
                            hours: ((period.totalHours ?? 0)).toFixed(2),
                            overtime: (period.overtimeHours ?? 0).toFixed(2),
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {period.status !== 'locked' ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleLockPeriod(period.id)}
                            disabled={lockPeriodMutation.isLoading}
                          >
                            {t('payrollBridge:timekeeping.lockAction', { defaultValue: 'Lock period' })}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {period.lockedAt ? formatDate(period.lockedAt) : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>
              {t('payrollBridge:newHire.title', { defaultValue: 'New-hire reporting' })}
            </CardTitle>
            <CardDescription>
              <span className="block">
                {t('payrollBridge:newHire.subtitle', {
                  defaultValue: 'Generate state reporting packets and track submission status.',
                })}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {t('payrollBridge:newHire.planLabel', {
                  defaultValue: 'Current plan: {{plan}}',
                  plan: planDisplayName,
                })}
              </span>
            </CardDescription>
          </div>
          {hasNewHireReporting ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => newHireReportsQuery.refetch()}
              disabled={newHireReportsLoading}
            >
              {t('common:buttons.reload', { defaultValue: 'Reload' })}
            </Button>
          ) : null}
        </CardHeader>

        {entitlementsQuery.isLoading ? (
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t('payrollBridge:newHire.loadingEntitlements', {
                defaultValue: 'Loading plan details…',
              })}
            </p>
          </CardContent>
        ) : hasNewHireReporting ? (
          <CardContent className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-[320px,minmax(0,1fr)]">
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="new-hire-employee">
                    {t('payrollBridge:newHire.employeeLabel', { defaultValue: 'Employee' })}
                  </Label>
                  <Select
                    value={selectedNewHireEmployee}
                    onValueChange={setSelectedNewHireEmployee}
                    disabled={employees.length === 0}
                  >
                    <SelectTrigger id="new-hire-employee">
                      <SelectValue
                        placeholder={t('payrollBridge:newHire.employeePlaceholder', {
                          defaultValue: 'Select employee',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((entry) => (
                        <SelectItem key={entry.id} value={entry.id}>
                          {entry.legalName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              <div className="grid gap-2">
                <Label htmlFor="new-hire-state">
                  {t('payrollBridge:newHire.stateLabel', { defaultValue: 'Filing state' })}
                </Label>
                <Select
                  value={selectedNewHireStateCode}
                  onValueChange={setSelectedNewHireStateCode}
                  disabled={newHireConfigs.length === 0}
                >
                  <SelectTrigger id="new-hire-state">
                    <SelectValue
                      placeholder={t('payrollBridge:newHire.statePlaceholder', {
                        defaultValue: 'Select state',
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {newHireConfigs.map((config) => (
                      <SelectItem key={config.stateCode} value={config.stateCode}>
                        {config.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="new-hire-method">
                  {t('payrollBridge:newHire.methodLabel', {
                    defaultValue: 'Preferred submission method',
                  })}
                </Label>
                <Select
                  value={selectedNewHireMethod}
                  onValueChange={(value) => setSelectedNewHireMethod(value as NewHireMethod)}
                  disabled={newHireSubmissionOptions.length === 0}
                >
                  <SelectTrigger id="new-hire-method">
                    <SelectValue
                      placeholder={t('payrollBridge:newHire.methodPlaceholder', {
                        defaultValue: 'Select method',
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {newHireSubmissionOptions.map((method) => (
                      <SelectItem key={method} value={method}>
                        {renderNewHireMethod(method)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleGenerateNewHireReport}
                disabled={newHireGenerateDisabled || isGeneratingNewHire}
              >
                {isGeneratingNewHire
                  ? t('payrollBridge:newHire.generating', { defaultValue: 'Generating…' })
                  : t('payrollBridge:newHire.generate', { defaultValue: 'Generate report' })}
              </Button>
            </div>

            <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-4 dark:border-border/40 dark:bg-muted/10">
              {selectedNewHireConfig ? (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-foreground">
                    {t('payrollBridge:newHire.instructionsTitle', {
                      defaultValue: '{{state}} filing steps',
                      state: selectedNewHireConfig.name,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('payrollBridge:newHire.deadline', {
                      defaultValue: 'Submit within {{days}} days of hire.',
                      days: selectedNewHireConfig.submissionDeadlineDays,
                    })}
                  </p>
                  {selectedNewHireConfig.portalUrl ? (
                    <p className="text-xs text-emerald-600 dark:text-emerald-300">
                      <a
                        href={selectedNewHireConfig.portalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        {selectedNewHireConfig.portalUrl}
                      </a>
                    </p>
                  ) : null}
                  <ul className="list-disc space-y-2 pl-5 text-xs text-muted-foreground">
                    {selectedNewHireConfig.instructions.map((instruction, index) => (
                      <li key={index}>{instruction}</li>
                    ))}
                  </ul>
                  {selectedNewHireConfig.notes ? (
                    <p className="text-xs text-muted-foreground">{selectedNewHireConfig.notes}</p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('payrollBridge:newHire.noStates', {
                    defaultValue: 'State configuration not available.',
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground">
                {t('payrollBridge:newHire.reportsTitle', { defaultValue: 'Submission history' })}
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => newHireReportsQuery.refetch()}
                disabled={newHireReportsLoading}
              >
                {t('common:buttons.reload', { defaultValue: 'Reload' })}
              </Button>
            </div>
            {newHireReportsLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:newHire.loading', { defaultValue: 'Loading reports…' })}
              </p>
            ) : newHireReports.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:newHire.noReports', { defaultValue: 'No new-hire reports yet.' })}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('payrollBridge:newHire.table.employee', { defaultValue: 'Employee' })}</TableHead>
                      <TableHead>{t('payrollBridge:newHire.table.state', { defaultValue: 'State' })}</TableHead>
                      <TableHead>{t('payrollBridge:newHire.table.status', { defaultValue: 'Status' })}</TableHead>
                      <TableHead>{t('payrollBridge:newHire.table.generated', { defaultValue: 'Generated' })}</TableHead>
                      <TableHead>{t('payrollBridge:newHire.table.submitted', { defaultValue: 'Submitted' })}</TableHead>
                      <TableHead className="text-right">
                        {t('payrollBridge:newHire.table.actions', { defaultValue: 'Actions' })}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newHireReports.map((report) => {
                      const submissionState =
                        newHireSubmissionForm[report.id] ??
                        ({
                          method: (report.submissionMethod ??
                            (newHireConfigs.find((cfg) => cfg.stateCode === report.stateCode)?.submissionMethods[0] ??
                              'portal')) as NewHireMethod,
                          reference: '',
                        } as { method: NewHireMethod; reference: string });
                      const reportConfig =
                        newHireConfigs.find((cfg) => cfg.stateCode === report.stateCode) ?? null;
                      const rowMethods = reportConfig?.submissionMethods ?? newHireSubmissionOptions;
                      const downloadHref = `/api/payroll_bridge/new_hire/reports/${report.id}/download`;
                      return (
                        <TableRow key={report.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-foreground">{report.employeeName}</span>
                              {report.employeeEmail ? (
                                <span className="text-xs text-muted-foreground">{report.employeeEmail}</span>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {reportConfig?.name ?? report.stateCode}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={NEW_HIRE_STATUS_STYLES[report.status]}
                            >
                              {t(`payrollBridge:newHire.status.${report.status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(report.generatedAt)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {report.submittedAt ? formatDateTime(report.submittedAt) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col items-end gap-2">
                              <Button asChild variant="link" size="sm" className="px-0">
                                <a href={downloadHref} target="_blank" rel="noreferrer">
                                  {t('payrollBridge:newHire.download', { defaultValue: 'Download PDF' })}
                                </a>
                              </Button>
                              {report.status !== 'submitted' ? (
                                <div className="flex w-full flex-col gap-2 lg:w-60">
                                  <Select
                                    value={submissionState.method}
                                    onValueChange={(value) =>
                                      handleSubmissionMethodChange(report.id, value as NewHireMethod)
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue
                                        placeholder={t('payrollBridge:newHire.methodPlaceholder', {
                                          defaultValue: 'Select method',
                                        })}
                                      />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {rowMethods.map((method) => (
                                        <SelectItem key={`${report.id}-${method}`} value={method}>
                                          {renderNewHireMethod(method)}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Input
                                    value={submissionState.reference}
                                    onChange={(event) =>
                                      handleSubmissionReferenceChange(report.id, event.target.value)
                                    }
                                    placeholder={t('payrollBridge:newHire.referencePlaceholder', {
                                      defaultValue: 'Confirmation number (optional)',
                                    })}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => handleSubmitNewHireReport(report)}
                                    disabled={submittingReportId === report.id}
                                  >
                                    {submittingReportId === report.id
                                      ? t('payrollBridge:newHire.markingSubmitted', {
                                          defaultValue: 'Saving…',
                                        })
                                      : t('payrollBridge:newHire.markSubmitted', {
                                          defaultValue: 'Mark submitted',
                                        })}
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </CardContent>
        ) : (
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center dark:border-emerald-500/40 dark:bg-emerald-500/10">
              <h3 className="text-base font-semibold text-emerald-900 dark:text-emerald-100">
                {t('payrollBridge:newHire.upsellTitle', {
                  defaultValue: 'Unlock state reporting with Payroll Sync',
                })}
              </h3>
              <p className="mt-2 text-sm text-emerald-900 dark:text-emerald-100">
                {t('payrollBridge:newHire.upsellDescription', {
                  defaultValue: 'Upgrade your plan to automate new-hire filings across supported states.',
                })}
              </p>
              <p className="mt-4 text-xs uppercase tracking-wide text-emerald-900 dark:text-emerald-100">
                {t('payrollBridge:newHire.planLabel', {
                  defaultValue: 'Current plan: {{plan}}',
                  plan: planDisplayName,
                })}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:newHire.upsellHint', {
                  defaultValue: 'Payroll Sync adds automated filings, export push delivery, and compliance alerts.',
                })}
              </p>
              <Button onClick={handleUpsellClick} disabled={entitlementCtaMutation.isLoading}>
                {entitlementCtaMutation.isLoading
                  ? t('payrollBridge:newHire.upsellCtaSubmitting', { defaultValue: 'Contacting…' })
                  : t('payrollBridge:newHire.upsellCta', { defaultValue: 'Talk to sales' })}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
      </div>
    </>
  );
};

export default PayrollManagerDashboard;
